import { HeartHandshakeIcon, MegaphoneIcon, UsersIcon, type LucideIcon } from 'lucide-react';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import type { EngagementPathway } from '@/content/engagement';
import { cn } from '@/lib/utils';

const iconById: Record<EngagementPathway['id'], LucideIcon> = {
  give: HeartHandshakeIcon,
  partner: UsersIcon,
  advocate: MegaphoneIcon,
  navigator: UsersIcon,
  general: UsersIcon
};

const toneClass: Record<EngagementPathway['tone'], string> = {
  cta: 'border-cta/35 bg-cta/10 text-cta',
  primary: 'border-primary/35 bg-primary/10 text-primary',
  violet: 'border-urgency/35 bg-urgency/10 text-urgency'
};

type PathwayCardsProps = {
  pathways: EngagementPathway[];
  headingId?: string;
  heading?: string;
};

export function PathwayCards({
  pathways,
  headingId = 'pathway-cards-heading',
  heading = 'How you can help'
}: PathwayCardsProps) {
  return (
    <section aria-labelledby={headingId} className="px-4 py-12 sm:px-6 sm:py-16 lg:px-8">
      <div className="mx-auto w-full max-w-7xl">
        <h2
          id={headingId}
          className="font-display text-foreground mb-8 text-2xl font-semibold tracking-tight sm:text-3xl"
        >
          {heading}
        </h2>
        <div className="grid gap-5 lg:grid-cols-3">
          {pathways.map((pathway) => {
            const Icon = iconById[pathway.id] ?? UsersIcon;
            const isExternal = Boolean(pathway.external);

            return (
              <article
                key={pathway.id}
                id={pathway.anchor}
                className="border-border/70 bg-card/55 scroll-mt-24 flex flex-col rounded-2xl border p-6 shadow-sm backdrop-blur-sm sm:p-7"
              >
                <div
                  className={cn(
                    'mb-5 inline-flex size-11 items-center justify-center rounded-xl border',
                    toneClass[pathway.tone]
                  )}
                >
                  <Icon className="size-5" aria-hidden />
                </div>
                <h3 className="font-display text-foreground text-xl font-semibold tracking-tight">
                  {pathway.title}
                </h3>
                <p className="text-foreground/90 mt-2 text-sm font-medium leading-relaxed">
                  {pathway.summary}
                </p>
                <p className="text-muted-foreground mt-3 flex-1 text-sm leading-relaxed">
                  {pathway.body}
                </p>
                <div className="mt-6">
                  <Button
                    asChild
                    variant={
                      pathway.tone === 'cta'
                        ? 'cta'
                        : pathway.tone === 'primary'
                          ? 'default'
                          : 'outline'
                    }
                    className="w-full sm:w-auto"
                  >
                    {isExternal ? (
                      <a href={pathway.href} target="_blank" rel="noreferrer noopener">
                        {pathway.ctaLabel}
                        <span className="sr-only">(opens in new tab)</span>
                      </a>
                    ) : (
                      <Link to={pathway.href}>{pathway.ctaLabel}</Link>
                    )}
                  </Button>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
